// src/utils/redactBankBalance.js
// ============================================================================
// Redact bank-disclosed account balances from a payment-alert email body.
//
// Bank credit alerts (HDFC and others) echo the running account balance, which
// must NEVER surface in the Payment Inbox — only the transaction amount matters
// for reconciliation. This removes any line that discloses a balance, whole and
// silent (no placeholder is left behind), regardless of the exact wording banks
// use, e.g.:
//   "Available Balance: INR 43,98,102.45"
//   "The available balance in your account is Rs. 43,98,102.45 as on ..."
//   "Avbl Bal INR 43,98,102.45"   /   "Avl Bal: 43,98,102.45"
//   "Closing / Current / Net Balance ..."
//
// Matching is case-insensitive and applied per line, so it works no matter
// where the balance sits in the body. Footer boilerplate that mentions
// "account balance" without a figure (e.g. "For real-time account balance,
// call 1800 270 3333") is intentionally left alone — it carries no amount.
//
// This is the read-time guard for rows ingested before balances were purged at
// source; new events are already scrubbed by supabase/functions/ingest-bank-email.
// Keep the two in sync, and keep this covered by
// src/utils/__tests__/redactBankBalance.test.js — it has regressed before.
// ============================================================================

// A balance label word (avbl / avl / available / closing / current / net)
// immediately followed by "bal" / "balance" marks a balance-disclosure line.
// The trailing \n? consumes the line's newline so no blank gap is left behind.
const BALANCE_LINE_RE =
  /^[^\n]*\b(?:avbl|avl|available|closing|current|net)\.?[ \t]*bal(?:ance)?\b[^\n]*\n?/gim;

/**
 * Remove every account-balance disclosure line from an email body. Returns the
 * input unchanged when it is empty/nullish.
 *
 * @param {string|null|undefined} body - raw email body text
 * @returns {string|null|undefined} body with balance lines removed
 */
export function redactBankBalance(body) {
  if (!body) return body;
  return body.replace(BALANCE_LINE_RE, '');
}

export default redactBankBalance;
